import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getDoc,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "firebase.js";
import * as Sentry from "@sentry/react";

export const createMatchesFromTeams = async (tournament) => {
  try {
    const divisions = Object.keys(tournament.teams);

    for (const division of divisions) {
      const teams = tournament.teams[division];

      for (let i = 0; i < teams.length; i++) {
        for (let j = i + 1; j < teams.length; j++) {
          const team1 = teams[i];
          const team2 = teams[j];

          const type =
            team1.players.length === 1 && team2.players.length === 1
              ? "singles"
              : "doubles";

          const match = {
            tournamentId: tournament.id,
            division,
            type,
            team1: {
              teamNumber: team1.teamNumber,
              players: team1.players.map((p) => ({
                id: p.userId,
                name: p.name || p.userId,
                ranking: p.ranking ?? 0,
              })),
            },
            team2: {
              teamNumber: team2.teamNumber,
              players: team2.players.map((p) => ({
                id: p.userId,
                name: p.name || p.userId,
                ranking: p.ranking ?? 0,
              })),
            },
            result: null,
            createdAt: new Date().toISOString(),
          };

          await addDoc(collection(db, "matches"), match);
        }
      }
    }
  } catch (error) {
    Sentry.captureException(error);
    console.error("❌ Failed to create matches:", error);
  }
};

export const getMatchesByTournamentId = async (tournamentId) => {
  if (!tournamentId) return [];

  const q = query(
    collection(db, "matches"),
    where("tournamentId", "==", tournamentId)
  ); // ➡️ Linking nextMatchId and auto-advancing BYEs stays the same

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};
export const deleteMatchesByTournamentId = async (tournamentId) => {
  if (!tournamentId) return;

  const q = query(
    collection(db, "matches"),
    where("tournamentId", "==", tournamentId)
  );

  const snapshot = await getDocs(q);
  const deletions = snapshot.docs.map((docSnap) =>
    deleteDoc(doc(db, "matches", docSnap.id))
  );

  await Promise.all(deletions);
};

export const updateMatchResult = async (matchId, result) => {
  const match = await fetchMatchById(matchId);

  // 1. Update current match
  const matchRef = doc(db, "matches", matchId);
  await updateDoc(matchRef, {
    result,
    updatedAt: new Date().toISOString(),
  });

  // 2. Auto-advance if elimination phase
  if (match.phase === "elimination") {
    // ➡️ Advance winner to next match if exists
    if (match.nextMatchId) {
      await advanceWinnerToNextMatch(match, result);
    }

    // ➡️ Advance loser to third place match if semifinal
    if (match.thirdPlaceMatchId) {
      await advanceLoserToThirdPlaceMatch(match, result);
    }
  }
};

const advanceWinnerToNextMatch = async (match, newResult) => {
  if (!match.nextMatchId) return;

  const nextMatch = await fetchMatchById(match.nextMatchId);
  const nextMatchRef = doc(db, "matches", match.nextMatchId);

  const winner = determineWinnerName(match, newResult);
  const loser = determineLoserName(match, newResult);

  const updates = {};

  if (!winner && !loser) {
    // 🛑 Reset teams if no result
    if (
      nextMatch.team1Name === match.team1Name ||
      nextMatch.team1Name === match.team2Name
    ) {
      updates.team1Name = "?";
      updates.team1 = null;
    }
    if (
      nextMatch.team2Name === match.team1Name ||
      nextMatch.team2Name === match.team2Name
    ) {
      updates.team2Name = "?";
      updates.team2 = null;
    }
  } else {
    // Remove loser if present
    if (nextMatch.team1Name === loser) {
      updates.team1Name = "?";
      updates.team1 = null;
    }
    if (nextMatch.team2Name === loser) {
      updates.team2Name = "?";
      updates.team2 = null;
    }

    // Insert winner if not already present
    const winnerAlreadyPresent =
      nextMatch.team1Name === winner || nextMatch.team2Name === winner;

    if (winner && winner !== "?" && !winnerAlreadyPresent) {
      const winnerTeamData =
        winner === match.team1Name ? match.team1 : match.team2;

      if (
        !nextMatch.team1Name ||
        nextMatch.team1Name === "?" ||
        updates.team1Name === "?"
      ) {
        updates.team1Name = winner;
        updates.team1 = winnerTeamData;
      } else if (
        !nextMatch.team2Name ||
        nextMatch.team2Name === "?" ||
        updates.team2Name === "?"
      ) {
        updates.team2Name = winner;
        updates.team2 = winnerTeamData;
      } else {
        console.warn(
          `⚠️ Cannot insert winner ${winner}, both slots are full in next match.`
        );
      }
    }
  }

  if (Object.keys(updates).length > 0) {
    await updateDoc(nextMatchRef, updates);
  }
};

const advanceLoserToThirdPlaceMatch = async (match, result) => {
  if (!match.thirdPlaceMatchId) return;

  const thirdPlaceRef = doc(db, "matches", match.thirdPlaceMatchId);
  const thirdPlaceDoc = await getDoc(thirdPlaceRef);

  if (!thirdPlaceDoc.exists()) {
    console.error("❌ Third place match not found!");
    return;
  }

  const thirdPlaceData = thirdPlaceDoc.data();
  const updates = {};

  const loser = determineLoserName(match, result);
  const winner = determineWinnerName(match, result);

  if (!winner && !loser) {
    // 🛑 Reset teams if no result
    if (
      thirdPlaceData.team1Name === match.team1Name ||
      thirdPlaceData.team1Name === match.team2Name
    ) {
      updates.team1Name = "Loser of SF1";
      updates.team1 = null;
    }
    if (
      thirdPlaceData.team2Name === match.team1Name ||
      thirdPlaceData.team2Name === match.team2Name
    ) {
      updates.team2Name = "Loser of SF2";
      updates.team2 = null;
    }
  } else {
    // Remove winner if somehow present (safety)
    if (thirdPlaceData.team1Name === winner) {
      updates.team1Name = "Loser of SF1";
      updates.team1 = null;
    }
    if (thirdPlaceData.team2Name === winner) {
      updates.team2Name = "Loser of SF2";
      updates.team2 = null;
    }

    // Insert loser if not already present
    const loserAlreadyPresent =
      thirdPlaceData.team1Name === loser || thirdPlaceData.team2Name === loser;

    if (!loserAlreadyPresent) {
      const loserTeamData =
        loser === match.team1Name ? match.team1 : match.team2;

      if (
        !thirdPlaceData.team1Name ||
        thirdPlaceData.team1Name === "Loser of SF1" ||
        updates.team1Name === "Loser of SF1"
      ) {
        updates.team1Name = loser;
        updates.team1 = loserTeamData;
      } else if (
        !thirdPlaceData.team2Name ||
        thirdPlaceData.team2Name === "Loser of SF2" ||
        updates.team2Name === "Loser of SF2"
      ) {
        updates.team2Name = loser;
        updates.team2 = loserTeamData;
      } else {
        console.warn(
          `⚠️ Cannot insert loser ${loser}, both slots are full in third place match.`
        );
      }
    }
  }

  if (Object.keys(updates).length > 0) {
    await updateDoc(thirdPlaceRef, updates);
  }
};

const fetchMatchById = async (matchId) => {
  const matchRef = doc(db, "matches", matchId);
  const matchSnap = await getDoc(matchRef);

  if (!matchSnap.exists()) {
    throw new Error(`Match with ID ${matchId} not found`);
  }

  return matchSnap.data();
};

const determineWinnerName = (match, result) => {
  const team1Score = result?.team1 ?? 0;
  const team2Score = result?.team2 ?? 0;

  if (team1Score > team2Score) {
    return match.team1Name;
  } else if (team2Score > team1Score) {
    return match.team2Name;
  } else {
    return null; // Tie or unfinished
  }
};

const determineLoserName = (match, result) => {
  if (!result || (result.team1 == null && result.team2 == null)) return null;

  const t1 = result?.team1 ?? 0;
  const t2 = result?.team2 ?? 0;

  if (t1 > t2) {
    return (
      match.team2Name || match.team2?.players?.map((p) => p.name).join(" & ")
    );
  } else if (t2 > t1) {
    return (
      match.team1Name || match.team1?.players?.map((p) => p.name).join(" & ")
    );
  } else {
    // 0-0 or tie: no loser
    return null;
  }
};

export const deleteEliminationMatches = async ({
  tournamentId,
  divisionName,
}) => {
  const q = query(
    collection(db, "matches"),
    where("tournamentId", "==", tournamentId),
    where("phase", "==", "elimination"),
    where("division", "==", divisionName)
  );

  const snapshot = await getDocs(q);

  const deletions = snapshot.docs.map((docSnap) =>
    deleteDoc(doc(db, "matches", docSnap.id))
  );

  await Promise.all(deletions);
};

export const fetchDivisionStats = async (tournamentId, division) => {
  const q = query(
    collection(db, "matches"),
    where("tournamentId", "==", tournamentId)
  );

  const snapshot = await getDocs(q);

  const matches = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  const stats = calculateStats(matches, division);

  stats.sort((a, b) => {
    if (b.wins !== a.wins) return b.wins - a.wins;
    return b.difference - a.difference;
  });

  return { stats, matches };
};

export const calculateStats = (matches, division) => {
  const stats = {};

  matches
    .filter((match) => {
      if (division === "merged") {
        return match.result; // take all matches if merged
      }
      return match.division === division && match.result;
    })
    .forEach((match) => {
      const { team1, team2, result } = match;
      const team1Name = team1.players.map((p) => p.name).join(" & ");
      const team2Name = team2.players.map((p) => p.name).join(" & ");

      if (!stats[team1Name])
        stats[team1Name] = { team: team1Name, wins: 0, scored: 0, conceded: 0 };
      if (!stats[team2Name])
        stats[team2Name] = { team: team2Name, wins: 0, scored: 0, conceded: 0 };

      const t1Score = result.team1 ?? 0;
      const t2Score = result.team2 ?? 0;

      stats[team1Name].scored += t1Score;
      stats[team1Name].conceded += t2Score;

      stats[team2Name].scored += t2Score;
      stats[team2Name].conceded += t1Score;

      if (t1Score > t2Score) stats[team1Name].wins += 1;
      else if (t2Score > t1Score) stats[team2Name].wins += 1;
    });

  return Object.values(stats).map((stat) => ({
    ...stat,
    difference: stat.scored - stat.conceded,
  }));
};

// 🧹 Cleaned main function
export const createEliminationMatches = async ({
  tournamentId,
  divisionName,
  bracketSize = 8,
}) => {
  if (!tournamentId || !divisionName) {
    throw new Error(
      "Invalid tournament or division to create elimination matches."
    );
  }

  if (divisionName === "merged") {
    await createMergedElimination(tournamentId, bracketSize);
  } else {
    await createSimpleElimination(tournamentId, divisionName, bracketSize);
  }
};

const createMergedElimination = async (tournamentId, bracketSize) => {
  const { stats: division1Stats, matches: division1Matches } =
    await fetchDivisionStats(tournamentId, "division1");
  const { stats: division2Stats, matches: division2Matches } =
    await fetchDivisionStats(tournamentId, "division2");

  const allMatches = [...division1Matches, ...division2Matches];

  const halfSize = bracketSize / 2;
  let topDivision1 = division1Stats.map((s) => s.team).slice(0, halfSize);
  let topDivision2 = division2Stats.map((s) => s.team).slice(0, halfSize);

  while (topDivision1.length < halfSize) topDivision1.push("BYE");
  while (topDivision2.length < halfSize) topDivision2.push("BYE");

  const firstRoundMatches = crossPoolsAndHandleByes(topDivision1, topDivision2);
  const bracketTree = buildBracketTreeFromFirstRound(firstRoundMatches);

  await saveBracket(allMatches, tournamentId, "merged", bracketTree);
};

const createSimpleElimination = async (
  tournamentId,
  divisionName,
  bracketSize
) => {
  const { stats, matches } = await fetchDivisionStats(
    tournamentId,
    divisionName
  );

  if (stats.length === 0) {
    throw new Error(`Aucune équipe disponible pour ${divisionName}`);
  }

  const realTeams = stats.map((s) => s.team).slice(0, bracketSize);

  while (realTeams.length < bracketSize) {
    realTeams.push("BYE");
  }

  const bracketTree = buildBracketTreeFromTeams(realTeams);

  await saveBracket(matches, tournamentId, divisionName, bracketTree);
};

const crossPoolsAndHandleByes = (poolA, poolB) => {
  const matches = [];
  const n = poolA.length;

  for (let i = 0; i < n; i++) {
    const teamA = poolA[i];
    const teamB = poolB[n - i - 1];
    matches.push([teamA, teamB]);
  }

  return matches;
};

// 🛠 Build bracket tree directly from first-round matches
const buildBracketTreeFromFirstRound = (firstRoundMatches) => {
  const matches = [];
  let matchId = 1;

  const firstRound = firstRoundMatches.map(([team1, team2]) => ({
    id: matchId++,
    team1Name: team1,
    team2Name: team2,
    round: getRoundName(firstRoundMatches.length * 2),
    nextMatchId: null,
    slotInNextMatch: null,
  }));

  matches.push(firstRound);

  let previousRound = firstRound;
  let semifinals = [];

  while (previousRound.length > 1) {
    const nextRound = [];
    for (let i = 0; i < previousRound.length; i += 2) {
      const nextMatch = {
        id: matchId++,
        team1Name: "?",
        team2Name: "?",
        round: getRoundName(previousRound.length),
        nextMatchId: null,
        slotInNextMatch: null,
      };

      previousRound[i].nextMatchId = nextMatch.id;
      previousRound[i].slotInNextMatch = "team1Name";

      previousRound[i + 1].nextMatchId = nextMatch.id;
      previousRound[i + 1].slotInNextMatch = "team2Name";

      nextRound.push(nextMatch);
    }

    // Save semifinals before moving to the final
    if (nextRound.length === 1) {
      semifinals = previousRound;
    }

    matches.push(nextRound);
    previousRound = nextRound;
  }

  // ➡️ Add third place match
  if (semifinals.length === 2) {
    const thirdPlaceMatch = {
      id: matchId++,
      team1Name: "Loser of SF1",
      team2Name: "Loser of SF2",
      round: "third_place",
      nextMatchId: null,
      slotInNextMatch: null,
    };

    matches.push([thirdPlaceMatch]);

    // ✅ Link semifinals to third place
    semifinals.forEach((semi) => {
      semi.thirdPlaceMatchId = thirdPlaceMatch.id;
    });
  }

  return matches.flat();
};

// 🛠 Build bracket tree from simple seeding
const buildBracketTreeFromTeams = (teams) => {
  const matches = [];
  let matchId = 1;

  const n = teams.length;
  const firstRound = getFirstRoundPairs(n).map(([seed1, seed2]) => ({
    id: matchId++,
    team1Name: teams[seed1 - 1] || "BYE",
    team2Name: teams[seed2 - 1] || "BYE",
    round: getRoundName(n),
    nextMatchId: null,
    slotInNextMatch: null,
  }));

  matches.push(firstRound);

  let previousRound = firstRound;
  let semifinals = [];

  while (previousRound.length > 1) {
    const nextRound = [];
    for (let i = 0; i < previousRound.length; i += 2) {
      const nextMatch = {
        id: matchId++,
        team1Name: "?",
        team2Name: "?",
        round: getRoundName(previousRound.length),
        nextMatchId: null,
        slotInNextMatch: null,
      };

      previousRound[i].nextMatchId = nextMatch.id;
      previousRound[i].slotInNextMatch = "team1Name";

      previousRound[i + 1].nextMatchId = nextMatch.id;
      previousRound[i + 1].slotInNextMatch = "team2Name";

      nextRound.push(nextMatch);
    }

    // Save semifinals (before the final round)
    if (nextRound.length === 1) {
      semifinals = previousRound;
    }

    matches.push(nextRound);
    previousRound = nextRound;
  }

  // ➡️ Add third place match
  if (semifinals.length === 2) {
    const thirdPlaceMatch = {
      id: matchId++,
      team1Name: "Loser of SF1",
      team2Name: "Loser of SF2",
      round: "third_place",
      nextMatchId: null,
      slotInNextMatch: null,
    };

    matches.push([thirdPlaceMatch]);

    // ✅ Link semifinals losers to third place match
    semifinals.forEach((semi) => {
      semi.thirdPlaceMatchId = thirdPlaceMatch.id;
    });
  }

  return matches.flat();
};

// 📋 Get first round seeding order
const getFirstRoundPairs = (n) => {
  if (n === 2) return [[1, 2]];
  if (n === 4)
    return [
      [1, 4],
      [2, 3],
    ];
  if (n === 8)
    return [
      [1, 8],
      [4, 5],
      [2, 7],
      [3, 6],
    ];
  if (n === 16)
    return [
      [1, 16],
      [8, 9],
      [5, 12],
      [4, 13],
      [6, 11],
      [3, 14],
      [7, 10],
      [2, 15],
    ];
  if (n === 32)
    return [
      [1, 32],
      [16, 17],
      [8, 25],
      [9, 24],
      [5, 28],
      [12, 21],
      [4, 29],
      [13, 20],
      [6, 27],
      [11, 22],
      [3, 30],
      [14, 19],
      [7, 26],
      [10, 23],
      [2, 31],
      [15, 18],
    ];
  // fallback basic pairing
  const pairs = [];
  for (let i = 1; i <= n; i += 2) {
    pairs.push([i, i + 1]);
  }
  return pairs;
};

const saveBracket = async (
  matches,
  tournamentId,
  divisionName,
  bracketTree
) => {
  const matchesCollection = collection(db, "matches");
  const localIdToFirestoreId = {};

  // 🔵 (You still need to prepare teamDataByName from matches)
  const teamDataByName = {};

  matches.forEach((match) => {
    if (match.team1?.players) {
      const name = match.team1.players.map((p) => p.name).join(" & ");
      teamDataByName[name] = {
        teamNumber: match.team1.teamNumber ?? null,
        players: match.team1.players ?? [],
      };
    }
    if (match.team2?.players) {
      const name = match.team2.players.map((p) => p.name).join(" & ");
      teamDataByName[name] = {
        teamNumber: match.team2.teamNumber ?? null,
        players: match.team2.players ?? [],
      };
    }
  });

  // 🔥 Now saving matches
  for (const match of bracketTree) {
    const isBye = match.team1Name === "BYE" || match.team2Name === "BYE";
    const initialResult = isBye
      ? {
          team1: match.team1Name !== "BYE" ? 1 : 0,
          team2: match.team2Name !== "BYE" ? 1 : 0,
        }
      : null;

    // 🧠 If match.team1 is already set, reuse it. Otherwise fallback to teamDataByName
    const team1Data = match.team1 ?? teamDataByName[match.team1Name] ?? null;
    const team2Data = match.team2 ?? teamDataByName[match.team2Name] ?? null;

    const docRef = await addDoc(matchesCollection, {
      tournamentId,
      division: divisionName,
      type: "doubles",
      phase: "elimination",
      round: match.round,
      team1Name: match.team1Name,
      team2Name: match.team2Name,
      team1: team1Data,
      team2: team2Data,
      nextMatchId: null,
      slotInNextMatch: match.slotInNextMatch,
      thirdPlaceMatchId: null,
      result: initialResult,
      createdAt: new Date().toISOString(),
    });

    localIdToFirestoreId[match.id] = docRef.id;
    match.firestoreId = docRef.id;
  }

  // Step 2: Link matches
  for (const match of bracketTree) {
    const updates = {};

    if (match.nextMatchId) {
      updates.nextMatchId = localIdToFirestoreId[match.nextMatchId] || null;
    }

    if (match.thirdPlaceMatchId) {
      updates.thirdPlaceMatchId =
        localIdToFirestoreId[match.thirdPlaceMatchId] || null;
    }

    if (Object.keys(updates).length > 0) {
      const matchRef = doc(db, "matches", match.firestoreId);
      await updateDoc(matchRef, updates);
    }
  }

  // Step 3: Auto-advance BYE
  for (const match of bracketTree) {
    if (!match.nextMatchId) continue;

    const isTeam1Bye = match.team1Name === "BYE";
    const isTeam2Bye = match.team2Name === "BYE";

    if (isTeam1Bye && isTeam2Bye) continue;

    if (isTeam1Bye || isTeam2Bye) {
      const winnerTeamName = isTeam1Bye ? match.team2Name : match.team1Name;
      const winnerTeamData = isTeam1Bye
        ? teamDataByName[match.team2Name]
        : teamDataByName[match.team1Name];
      const teamDataField =
        match.slotInNextMatch === "team1Name" ? "team1" : "team2";

      const nextMatchRef = doc(
        db,
        "matches",
        localIdToFirestoreId[match.nextMatchId]
      );
      await updateDoc(nextMatchRef, {
        [match.slotInNextMatch]: winnerTeamName,
        [teamDataField]: winnerTeamData,
      });
    }
  }
};

// 🛠 Utility
const getRoundName = (size) => {
  switch (size) {
    case 2:
      return "final";
    case 4:
      return "semifinal";
    case 8:
      return "quarterfinal";
    case 16:
      return "round_of_16";
    case 32:
      return "round_of_32";
    default:
      return "first_round";
  }
};
